import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import {
  URUGUAY_BANKS,
  getUruguayBank,
  type BankCategory,
  type UruguayBank,
} from "@gestionalo/shared-types";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const CATEGORY_LABEL: Record<BankCategory, string> = {
  bank: "Bancos",
  casa_financiera: "Casas financieras",
  cooperativa: "Cooperativas",
  ede: "EDEs / billeteras",
};

const CATEGORY_ORDER: BankCategory[] = [
  "bank",
  "casa_financiera",
  "cooperativa",
  "ede",
];

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export interface BankComboboxProps {
  value: string;
  onChange: (code: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  ariaInvalid?: boolean;
}

export function BankCombobox({
  value,
  onChange,
  id,
  placeholder = "Seleccioná un banco",
  disabled,
  ariaInvalid,
}: BankComboboxProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = getUruguayBank(value);

  const grouped = useMemo(() => {
    const q = normalize(query.trim());
    const filtered = URUGUAY_BANKS.filter((b) => {
      if (!b.active) return false;
      if (!q) return true;
      return (
        normalize(b.shortName).includes(q) ||
        normalize(b.officialName).includes(q) ||
        b.code.includes(q)
      );
    });
    const map = new Map<BankCategory, UruguayBank[]>();
    for (const b of filtered) {
      const arr = map.get(b.category) ?? [];
      arr.push(b);
      map.set(b.category, arr);
    }
    return CATEGORY_ORDER.map((cat) => ({
      category: cat,
      label: CATEGORY_LABEL[cat],
      banks: (map.get(cat) ?? []).sort((a, b) =>
        a.shortName.localeCompare(b.shortName, "es"),
      ),
    })).filter((g) => g.banks.length > 0);
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="truncate">
            {selected ? (
              <>
                <span className="font-medium">{selected.shortName}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {selected.code}
                </span>
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar banco o código"
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
            autoFocus
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {grouped.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              Sin resultados
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.category} className="py-1">
                <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </div>
                {group.banks.map((b) => {
                  const isSelected = b.code === value;
                  return (
                    <button
                      key={b.code}
                      type="button"
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-accent/50",
                      )}
                      onClick={() => {
                        onChange(b.code);
                        setOpen(false);
                        setQuery("");
                      }}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">
                          {b.shortName}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {b.code}
                          </span>
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {b.officialName}
                        </span>
                      </span>
                      {isSelected ? (
                        <Check className="ml-2 h-4 w-4 shrink-0" aria-hidden />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
